/** The fields grouping needs, so tests don't have to build a full `AssetDetails`. */
export interface GroupableServer {
	uniq_id: string;
	isPrivateParent?: boolean;
	isVmware?: boolean;
	isNutanixPrivateCloud?: boolean;
	isNutanixVm?: boolean;
	privateParent?: { uniq_id: string } | null;
	children?: { uniq_id: string }[];
}

export interface ServerListEntry<T extends GroupableServer> {
	server: T;
	/** True for a private cloud parent, even when none of its children are loaded. */
	isParent: boolean;
	/** The children that are loaded, in the order they were given. */
	children: T[];
	/** Every child the parent is linked to, loaded or not. */
	numberOfChildServers: number;
}

export interface GroupServersByParentOptions {
	/**
	 * Child ids per parent id for products whose children aren't on the asset
	 * itself (VMware vDC, via its portal details).
	 */
	linkedChildIds?: Record<string, string[]>;
}

const isParentServer = (server: GroupableServer) =>
	Boolean(
		server.isPrivateParent || server.isVmware || server.isNutanixPrivateCloud,
	);

/**
 * Groups private cloud children under their parent, keeping the order of
 * `servers`. A child whose parent isn't in `servers` stays a top-level entry,
 * so a search or filter that matches only the child still shows it.
 *
 * Nutanix has no parent link in the API, so its VMs are grouped only when
 * exactly one Nutanix VPC is present.
 */
const groupServersByParent = <T extends GroupableServer>(
	servers: T[],
	{ linkedChildIds = {} }: GroupServersByParentOptions = {},
): ServerListEntry<T>[] => {
	const serversById = new Map<string, T>();

	servers.forEach((server) => {
		if (!serversById.has(server.uniq_id)) {
			serversById.set(server.uniq_id, server);
		}
	});

	const parents = Array.from(serversById.values()).filter(isParentServer);
	const nutanixVpcs = parents.filter(({ isNutanixPrivateCloud }) =>
		Boolean(isNutanixPrivateCloud),
	);
	const nutanixVpcId =
		nutanixVpcs.length === 1 ? nutanixVpcs[0].uniq_id : undefined;

	const childIdsByParentId = new Map<string, string[]>();
	const parentIdByChildId = new Map<string, string>();

	const link = (parentId: string, childId: string) => {
		if (parentId === childId || parentIdByChildId.has(childId)) return;

		parentIdByChildId.set(childId, parentId);
		childIdsByParentId.set(parentId, [
			...(childIdsByParentId.get(parentId) ?? []),
			childId,
		]);
	};

	parents.forEach((parent) => {
		parent.children?.forEach(({ uniq_id }) => link(parent.uniq_id, uniq_id));
		linkedChildIds[parent.uniq_id]?.forEach((childId) =>
			link(parent.uniq_id, childId),
		);
	});

	serversById.forEach((server) => {
		const privateParentId = server.privateParent?.uniq_id;

		if (privateParentId && serversById.get(privateParentId)) {
			link(privateParentId, server.uniq_id);
		}

		if (nutanixVpcId && server.isNutanixVm) {
			link(nutanixVpcId, server.uniq_id);
		}
	});

	const entries: ServerListEntry<T>[] = [];
	const childrenByParentId = new Map<string, T[]>();

	serversById.forEach((server) => {
		const parentId = parentIdByChildId.get(server.uniq_id);

		// Only a loaded parent can show its child, otherwise the child stays flat
		if (parentId && serversById.has(parentId)) {
			childrenByParentId.set(parentId, [
				...(childrenByParentId.get(parentId) ?? []),
				server,
			]);

			return;
		}

		entries.push({
			server,
			isParent: isParentServer(server),
			children: [],
			numberOfChildServers: 0,
		});
	});

	return entries.map((entry) => ({
		...entry,
		children: childrenByParentId.get(entry.server.uniq_id) ?? [],
		numberOfChildServers:
			childIdsByParentId.get(entry.server.uniq_id)?.length ?? 0,
	}));
};

export default groupServersByParent;
